import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { Link } from "react-router-dom";

import utils from "./../../universal/utils";

import objectAssign from "object-assign";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import UploadButton from "./UploadField";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import PageShell from "../common/PageShell";
import RefragPromoNotice from "../common/RefragPromoNotice";
import MembershipSection from "../membership/MembershipSection";
import TwoFactorSetup from "./TwoFactorSetup";
import AccountDataDeletionSection from "./AccountDataDeletionSection";
import UiDesignPreferenceToggle from "./UiDesignPreferenceToggle";
import MatchStartSoundToggle from "./MatchStartSoundToggle";
import {
  calculateAgeFromDob as calculateAgeFromDobValue,
  formatDateOfBirthForSubmit,
  getAccountSaveValidationError,
  getNicknameValidationError,
  NICKNAME_HINT,
  NICKNAME_MAX_LENGTH,
  isPendingReviewPhoneCorrectionMode as accountPendingReviewPhoneCorrectionMode,
  isValidInternationalPhoneNumber,
  getInternationalPhoneValidationError,
  normalizeDateOfBirthValue,
  PENDING_REVIEW_PROFILE_EDITABLE_FIELDS,
} from "./accountFormUtils";
import {
  ACCOUNT_VERIFICATION_FACEIT_FIELD_LABEL,
  ACCOUNT_VERIFICATION_FACEIT_HELPER_TEXT,
  ACCOUNT_VERIFICATION_GUIDANCE_INTRO,
  ACCOUNT_VERIFICATION_GUIDANCE_ITEMS,
  ACCOUNT_VERIFICATION_GUIDANCE_TITLE,
  ACCOUNT_VERIFICATION_STEAM_PROFILE_URL_FIELD_LABEL,
  ACCOUNT_VERIFICATION_STEAM_PROFILE_URL_HELPER_TEXT,
  ACCOUNT_VERIFICATION_STEAMID_IMAGE_ALT,
  ACCOUNT_VERIFICATION_STEAMID_IMAGE_SRC,
} from "./accountVerificationContent";
import {
  getUnlikelyAcceptanceCopy,
  hasHiddenSteamGameDetails,
  hasUnlikelySteamSignupWarnings,
  STEAM_PRIVACY_SETTINGS_URL,
} from "../auth/steamSignupNotices";
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
  shouldResetAvatarPreview,
} from "../../utils/avatarUrl";
import KycVerificationCard from "../kyc/KycVerificationCard";
import SteamProfileCompletion from "./SteamProfileCompletion";
import PushNotificationOptIn from "./PushNotificationOptIn";
import {
  buildReferralInviteLink,
  REFERRAL_STORAGE_KEY,
} from "../../universal/referralLinks";
import { fetchSteamChecks } from "../../redux/actions/catalogActions";

const ELIGIBILITY_THREAD_FETCH_INTERVAL_MS = 30000;
const DIDIT_PHONE_VERIFICATION_UI_ENABLED = false;
const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {},
  homepageRoot: {
    minHeight: 560,
    marginTop: 0,
  },
  contentWidth: {
    margin: "0 auto",
    width: "100%",
  },
  alert: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  button: {
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  formGroup: {
    display: "block",
    marginTop: 0,
    marginLeft: "auto",
    marginRight: "auto",
  },
  lobbyContainer: {
    display: "flex",
    width: "100%",
    maxWidth: 740,
    margin: "0 auto",
  },
  avatar: {
    width: 82,
    height: 82,
    margin: "15px auto",
    position: "relative",
    right: 1,
  },
  lobbyPersonBox: {
    width: 180,
    height: 190,
    marginRight: 20,
    borderRadius: 10,
    border: "solid 1px #adadad",
  },
  inviteLinkBtn: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  queueButton: {
    margin: "0 auto",
    fontSize: "24pt",
    padding: "4px 16px",
  },
  queueButtonCont: {
    margin: "0 auto",
    position: "relative",
    width: 150,
  },
  queueTimerCont: {
    border: "solid 1px #b5b1b3",
    borderRadius: 8,
    fontFamily: "Roboto",
    fontWeight: 525,
    fontSize: 28,
    width: 94,
    margin: "20px auto",
    padding: 16,
    position: "relative",
  },
  matchmakingOptionsCont: {
    border: "solid 1px #b5b1b3",
    borderRadius: 8,
    margin: "35px auto",
    position: "relative",
    textAlign: "left",
    paddingLeft: 10,
    paddingBottom: 10,
    paddingTop: 10,
    minHeight: 200,
    width: "100%",
    maxWidth: 740,
  },
  matchmakingSectionTitle: {
    position: "absolute",
    top: -23,
    fontWeight: 525,
  },
  anim: {
    position: "absolute",
    top: 13,
    right: -35,
  },
  verificationBorder: {
    border: "1px solid rgba(71, 122, 181, 0.65)",
    position: "relative",
    padding: theme.spacing(2),
    borderRadius: 10,
    width: "100%",
    boxSizing: "border-box",
  },
  vspacer10: {
    height: 10,
  },
  selfieExplanation: {
    marginTop: theme.spacing(1),
    lineHeight: 1.5,
    color: "rgba(255,255,255,0.85)",
  },
  updateAvatarBox: {
    border: "1px solid rgba(255,255,255,0.14)",
    position: "relative",
    width: "100%",
    boxSizing: "border-box",
    minHeight: 56,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  updateAvatarLabel: {
    position: "absolute",
    top: -10,
    fontSize: 13,
    left: 12,
    color: "rgba(255,255,255,0.65)",
    background: theme.palette.background.paper,
    paddingRight: 6,
    paddingLeft: 6,
  },
  existingAvatar: {
    height: 56,
    width: 56,
  },
  textFieldRegular: {
    marginRight: 0,
  },
  accountStatusWarning: {
    color: "#FF9800",
    fontWeight: "bold",
  },
  lobbyFrame: {
    margin: "12px auto",
    textAlign: "left",
    fontSize: "12pt",
    position: "relative",
    padding: theme.spacing(1.5),
    minHeight: 500,
    width: "100%",
    boxSizing: "border-box",
  },
  rejectionAlert: {
    marginTop: 7,
    width: "fit-content",
  },
  regularLink: {
    color: "#666",
  },
  buttonsCont: {
    marginBottom: 20,
  },
  accountInner: {
    marginLeft: 0,
  },
  verificationHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  verificationTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "rgba(255,255,255,0.85)",
  },
  uploadStatusRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    maxWidth: 520,
  },
  uploadStatusText: {
    fontSize: 12,
    color: "rgba(255,255,255,0.72)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  uploadStatusTextError: {
    fontSize: 12,
    color: "#ba0b0b",
    fontWeight: 700,
  },
  loadingAnim: {
    color: theme.palette.primary.main,
  },
  docUploadedIcon: {},
  "@media (min-width: 1024px)": {
    homepageRoot: {
      width: "100%",
      margin: 0,
    },
    formGroup: {
      display: "flex",
      justifyContent: "center",
    },
    lobbyFrame: {
      margin: "20px auto",
      textAlign: "left",
      fontSize: "14pt",
      position: "relative",
      padding: theme.spacing(3),
      minHeight: 500,
      width: "90%",
    },
    homeContainer: {
      display: "none",
    },
  },
});


const APPLICATION_FIELD_LABELS = {
  name: "Full Name",
  nickname: "In-game Nickname",
  dateOfBirth: "Date of Birth",
  country: "Country",
  steamId64: "Steam ID 64",
  steamProfileUrl: "Steam Profile URL",
  faceitProfileUrl: "FaceIT Profile URL",
  phoneNumber: "Phone Number",
  passport: "Identity Document (Passport)",
  selfie_video: "Selfie Video",
};

function EligibilityAppealSection({ user }) {
  const [thread, setThread] = React.useState(null);
  const [threadLoading, setThreadLoading] = React.useState(true);
  const [replyContent, setReplyContent] = React.useState("");
  const [replying, setReplying] = React.useState(false);
  const [replyError, setReplyError] = React.useState("");
  const [replySuccess, setReplySuccess] = React.useState(false);

  const fetchThread = React.useCallback(() => {
    fetch(`${basePath}/api/v1/eligibility/thread`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) setThread(data.thread || null);
      })
      .catch(() => {})
      .finally(() => setThreadLoading(false));
  }, []);

  React.useEffect(() => {
    fetchThread();
    const interval = setInterval(fetchThread, ELIGIBILITY_THREAD_FETCH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchThread]);

  const handleSendReply = async () => {
    const trimmed = replyContent.trim();
    if (!trimmed) return;
    setReplying(true);
    setReplyError("");
    setReplySuccess(false);
    try {
      const res = await fetch(`${basePath}/api/v1/eligibility/thread/reply`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setReplyError(data.message || "Failed to send your message. Please try again.");
      } else {
        setReplyContent("");
        setReplySuccess(true);
        fetchThread();
        setTimeout(() => setReplySuccess(false), 4000);
      }
    } catch {
      setReplyError("Network error. Please try again.");
    } finally {
      setReplying(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const notEligibleReason = user?.notEligibleReason || null;
  const messages = (thread?.messages || []).filter((m) => !m.isStatusUpdate || m.author === "admin");
  const isClosed = thread?.status === "closed";

  return (
    <Box sx={{ mb: 3 }}>
      <Alert
        severity="error"
        sx={{
          mb: 2,
          borderLeft: "4px solid #c62828",
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>Account Not Eligible</AlertTitle>
        Your account has been reviewed and is currently not eligible to participate in Ouro
        Matchmaking.
        {notEligibleReason && (
          <Box sx={{ mt: 1 }}>
            <strong>Reason:</strong> {notEligibleReason}
          </Box>
        )}
      </Alert>

      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderColor: "rgba(255,255,255,0.1)",
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          Eligibility Appeal
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, lineHeight: 1.6 }}>
          If you believe this decision is incorrect, you can send a message to our team below.
          We will review and respond in this thread.
        </Typography>

        {threadLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <>
            {messages.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                {messages.map((msg, i) => {
                  const isAdmin = msg.author === "admin";
                  return (
                    <Box
                      key={i}
                      sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        maxWidth: "88%",
                        backgroundColor: isAdmin
                          ? "rgba(232, 163, 61,0.08)"
                          : "rgba(255,255,255,0.05)",
                        alignSelf: isAdmin ? "flex-start" : "flex-end",
                        border: `1px solid ${isAdmin ? "rgba(232, 163, 61,0.18)" : "rgba(255,255,255,0.08)"}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontWeight: 700,
                          color: isAdmin ? "#E8A33D" : "rgba(255,255,255,0.5)",
                          mb: 0.5,
                        }}
                      >
                        {isAdmin ? msg.authorName || "Ouro Team" : "You"} &middot;{" "}
                        {formatDate(msg.timestamp)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}
                      >
                        {msg.content}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {messages.length === 0 && !isClosed && (
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.4)", fontStyle: "italic", mb: 2 }}
              >
                No messages yet. Send your first appeal message below.
              </Typography>
            )}

            {isClosed && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This appeal thread has been closed. If you have further questions please contact
                support.
              </Alert>
            )}

            {!isClosed && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  placeholder="Explain your situation or why you believe this decision should be reconsidered..."
                  value={replyContent}
                  onChange={(e) => {
                    setReplyContent(e.target.value);
                    if (replyError) setReplyError("");
                  }}
                  disabled={replying}
                  inputProps={{ maxLength: 3000 }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendReply}
                  disabled={replying || !replyContent.trim()}
                  sx={{ minWidth: 88, alignSelf: "flex-end", mb: 0.25 }}
                >
                  {replying ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Send"}
                </Button>
              </Box>
            )}

            {replyError && (
              <Typography variant="caption" sx={{ color: "#f44336", display: "block", mt: 0.5 }}>
                {replyError}
              </Typography>
            )}

            {replySuccess && (
              <Typography
                variant="caption"
                sx={{ color: "#4caf50", display: "block", mt: 0.5 }}
              >
                Your message was sent. Our team will respond here.
              </Typography>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

function ApplicationFeedbackBanner({ applicationCommunication }) {
  const { flaggedFields = [], message = "" } = applicationCommunication || {};

  const fieldLabels = flaggedFields
    .map((f) => APPLICATION_FIELD_LABELS[f] || f)
    .filter(Boolean);

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
    >
      <AlertTitle>Action required: update your application</AlertTitle>
      Our team has reviewed your application and is asking you to make some
      corrections before we can proceed.
      {fieldLabels.length > 0 && (
        <Box component="div" sx={{ mt: 1 }}>
          <strong>Please correct the following field(s):</strong>
          <Box
            component="ul"
            sx={{ m: 0, mt: 0.5, pl: 2.5 }}
          >
            {fieldLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </Box>
        </Box>
      )}
      {message && (
        <Box component="div" sx={{ mt: 1 }}>
          <strong>Notes from the Ouro team:</strong>
          <br />
          {message}
        </Box>
      )}
      <Box component="div" sx={{ mt: 1.5 }}>
        Once you have fixed the issues above, click{" "}
        <strong>"Re-submit Application"</strong> at the bottom of this page to
        send your updated application for review.
      </Box>
    </Alert>
  );
}

function getReadinessSuccessCopy(accountStatus, phoneVerificationStatus) {
  if (
    accountStatus === "pending_review" &&
    phoneVerificationStatus === "phone_verification_ready"
  ) {
    return "Your profile is under review. You can optionally complete phone verification to increase your trust factor.";
  }

  if (
    accountStatus === "pending_review" &&
    phoneVerificationStatus === "code_sent"
  ) {
    return "Your profile is under review. Enter the verification code to complete optional phone verification.";
  }

  if (
    accountStatus === "pending_review" &&
    phoneVerificationStatus === "expired"
  ) {
    return "Your profile details passed review, but your verification code expired. Click \"Send Verification Code\" next to your phone number to request a new one.";
  }

  if (accountStatus === "pending_review") {
    return "Your profile is complete and under review";
  }

  if (accountStatus === "rejected") {
    return "Your profile is complete. Save your latest changes to send it back for review.";
  }

  return "Your profile is complete and ready for review";
}

function getAccountStatusColor(accountStatus) {
  return accountStatusColors[accountStatus];
}

function getAccountStatusChipProps(accountStatus, accountDisplayStatus) {
  const label =
    typeof accountDisplayStatus === "string" && accountDisplayStatus.trim()
      ? accountDisplayStatus.trim()
      : typeof accountStatus === "string" && accountStatus.trim()
        ? accountStatus.replace(/_/g, " ")
        : "Status unavailable";
  const statusStyle =
    accountStatusChipStyles[accountStatus] || accountStatusChipStyles.default;

  return {
    label,
    sx: {
      height: "auto",
      minHeight: 32,
      borderRadius: 999,
      border: `1px solid ${statusStyle.borderColor}`,
      backgroundColor: statusStyle.backgroundColor,
      color: statusStyle.color,
      fontWeight: 700,
      "& .MuiChip-label": {
        display: "block",
        padding: "6px 12px",
        whiteSpace: "normal",
      },
    },
  };
}

function normalizePhoneForComparison(phoneNumber) {
  if (typeof phoneNumber !== "string") return "";

  const trimmed = phoneNumber.trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("+")) {
    return `+${trimmed.slice(1).replace(/\D/g, "")}`;
  }

  return trimmed.replace(/\s+/g, " ");
}

function getTerminalPhoneVerificationStatus(message) {
  if (message === "Verification code has expired") {
    return "expired";
  }

  if (message === "Too many incorrect attempts") {
    return "expired";
  }

  return null;
}

function isActivePhoneVerificationMessage(message) {
  return (
    message ===
    "A verification code has already been sent to your phone. Please enter it below."
  );
}

function ReadinessChecklist({
  refreshToken,
  accountStatus,
  phoneVerificationStatus = null,
}) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setReadiness(null);
    async function fetchReadiness() {
      try {
        const res = await fetch(`${utils.getEdgeBasePath()}/api/v1/account/readiness`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => null);
        if (!cancelled && res.ok && data?.ok && data.readiness) {
          setReadiness(data.readiness);
        } else if (!cancelled) {
          setReadiness(null);
        }
      } catch (_) {
        if (!cancelled) {
          setReadiness(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchReadiness();
    return () => { cancelled = true; };
  }, [refreshToken]);

  if (loading || !readiness) return null;

  const { checks, completed, total, allDone, warningItems = [] } = readiness;
  const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Box sx={{ mb: 3 }}>
      <Paper variant="outlined" sx={{ p: 2, borderLeftWidth: 4, borderLeftStyle: "solid", borderLeftColor: allDone ? "success.main" : "info.main" }}>
        {allDone ? (
          <>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "success.main" }}>
              {getReadinessSuccessCopy(accountStatus, phoneVerificationStatus)}
            </Typography>
            {warningItems.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {warningItems.join(". ")}.
              </Alert>
            )}
          </>
        ) : (
          <>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Profile Readiness ({completed}/{total})
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ mb: 1.5, height: 8, borderRadius: 4 }}
            />
            <List dense disablePadding>
              {Object.entries(checks).map(([key, check]) => (
                <ListItem key={key} disableGutters sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {check.done ? (
                      <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
                    ) : (
                      <CancelIcon sx={{ color: "error.main", fontSize: 20 }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={check.warning ? `${check.label} (recommended)` : check.label}
                    secondary={check.warning ? check.warningLabel : null}
                    primaryTypographyProps={{ variant: "body2", color: check.done ? "text.secondary" : "text.primary" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Paper>
    </Box>
  );
}

function getProfileTrustFactor(user) {
  const value = Number(user?.profileTrustFactor);
  if (Number.isFinite(value)) {
    return Math.max(1, Math.min(10, value));
  }
  return 1;
}

function ProfileTrustFactorBar({ user, accountStatus, accountDisplayStatus }) {
  const score = getProfileTrustFactor(user);
  const percent = Math.round((score / 10) * 100);
  const onboardingTier = Number(user?.completedOnboardingTier) || 0;
  const profileTrustTier = user?.profileTrustTier || "incomplete";
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        mb: 2,
      }}
    >
      {/* Account status — 30% on desktop, full width on mobile */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          flex: { md: "0 0 30%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Account status
        </Typography>
        <Chip
          id="account-status"
          aria-label={`Account status: ${accountDisplayStatus}`}
          size="small"
          sx={{ alignSelf: "flex-start" }}
          {...getAccountStatusChipProps(accountStatus, accountDisplayStatus)}
        />
        {accountStatus === "pending_review" && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
            Your details are sufficient — your application is now with our team for human review. We&apos;ll notify you once it&apos;s complete.
          </Typography>
        )}
      </Paper>

      {/* Profile strength — 70% on desktop, full width on mobile */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, sm: 2 },
          flex: { md: "1 1 70%" },
          borderColor: "rgba(76, 175, 80, 0.35)",
          backgroundColor: "rgba(76, 175, 80, 0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Profile Strength
          </Typography>
          <Chip size="small" color={score >= 7 ? "success" : "default"} label={`${score.toFixed(2)}/10`} />
        </Box>
        <Box
          sx={{
            height: 10,
            borderRadius: 999,
            overflow: "hidden",
            backgroundColor: "rgba(255,255,255,0.12)",
          }}
        >
          <Box
            sx={{
              width: `${percent}%`,
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(90deg, #8a8f98 0%, #22c55e 100%)",
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Onboarding tier {onboardingTier} · {profileTrustTier.replace(/_/g, " ")}.
          Higher verification levels improve match quality and unlock the option
          to play only with similarly verified players.
        </Typography>
      </Paper>
    </Box>
  );
}

function ApplicationStatusBanner({ user, accountStatus }) {
  if (user?.signupSource !== "steam") return null;

  const hasEmail = user?.emailIsSynthetic !== true && !!user?.emailVerifiedAt;
  let severity = "info";
  let title = "Application status";
  let message = "Your application is pending. Sign back in with Steam to check the latest status.";

  if (accountStatus === "pending_review") {
    title = "Application pending review";
    message = hasEmail
      ? "Your application is with our team. We will email you when review is complete."
      : "Your application is with our team. Without an email on file, this page is where you will see the result.";
  } else if (accountStatus === "verified") {
    severity = "success";
    title = "Application approved";
    message = "Your application has been approved. Welcome to Ouro.";
  } else if (accountStatus === "rejected" || accountStatus === "noteligible") {
    severity = "warning";
    title = "Application not accepted";
    message = "Your application was not accepted. Check any feedback below or contact support if you think this is wrong.";
  } else if (accountStatus === "pending") {
    message = hasEmail
      ? "Complete the checklist below so your Steam profile can enter manual review."
      : "Email is optional, but without one you will need to return here to check review status.";
  }

  return (
    <Alert severity={severity} sx={{ mb: 2 }}>
      <AlertTitle>{title}</AlertTitle>
      {message}
    </Alert>
  );
}

function TierSection({ title, subtitle, tier, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Grid size={{ xs: 12 }} sx={{ order: tier * 100 }}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5 }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setOpen((value) => !value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setOpen((value) => !value);
            }
          }}
          sx={{
            minHeight: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            cursor: "pointer",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <Chip size="small" label={open ? "Hide" : "Show"} />
        </Box>
        {open ? (
          <Grid container rowSpacing={3} columnSpacing={{ xs: 2, md: 3 }} sx={{ mt: 2 }}>
            {children}
          </Grid>
        ) : null}
      </Paper>
    </Grid>
  );
}

const accountStatusColors = {
  pending: "#303030",
  pending_review: "#FF9800",
  verified: "#008f00",
  rejected: "#b31902",
  noteligible: "#4a0d4d",
  banned: "#000000",
};

const accountStatusChipStyles = {
  pending: {
    backgroundColor: "rgba(245, 158, 11, 0.14)",
    borderColor: "rgba(245, 158, 11, 0.55)",
    color: "#FBBF24",
  },
  pending_review: {
    backgroundColor: "rgba(255, 167, 38, 0.18)",
    borderColor: "rgba(255, 167, 38, 0.44)",
    color: "#A78BFA",
  },
  verified: {
    backgroundColor: "rgba(76, 175, 80, 0.18)",
    borderColor: "rgba(76, 175, 80, 0.4)",
    color: "#a5d6a7",
  },
  rejected: {
    backgroundColor: "rgba(244, 67, 54, 0.18)",
    borderColor: "rgba(244, 67, 54, 0.4)",
    color: "#ef9a9a",
  },
  noteligible: {
    backgroundColor: "rgba(171, 71, 188, 0.18)",
    borderColor: "rgba(171, 71, 188, 0.4)",
    color: "#e1bee7",
  },
  banned: {
    backgroundColor: "rgba(144, 164, 174, 0.22)",
    borderColor: "rgba(144, 164, 174, 0.36)",
    color: "#eceff1",
  },
  default: {
    backgroundColor: "rgba(255,255,255,0.08)",
    borderColor: "rgba(255,255,255,0.18)",
    color: "rgba(255,255,255,0.92)",
  },
};

const PHONE_DIAL_CODES = [
  { code: "AL", dialCode: "+355", name: "Albania" },
  { code: "AD", dialCode: "+376", name: "Andorra" },
  { code: "AR", dialCode: "+54",  name: "Argentina" },
  { code: "AM", dialCode: "+374", name: "Armenia" },
  { code: "AU", dialCode: "+61",  name: "Australia" },
  { code: "AT", dialCode: "+43",  name: "Austria" },
  { code: "BY", dialCode: "+375", name: "Belarus" },
  { code: "BE", dialCode: "+32",  name: "Belgium" },
  { code: "BA", dialCode: "+387", name: "Bosnia and Herzegovina" },
  { code: "BR", dialCode: "+55",  name: "Brazil" },
  { code: "BG", dialCode: "+359", name: "Bulgaria" },
  { code: "CA", dialCode: "+1",   name: "Canada" },
  { code: "CL", dialCode: "+56",  name: "Chile" },
  { code: "HR", dialCode: "+385", name: "Croatia" },
  { code: "CY", dialCode: "+357", name: "Cyprus" },
  { code: "CZ", dialCode: "+420", name: "Czech Republic" },
  { code: "DK", dialCode: "+45",  name: "Denmark" },
  { code: "EE", dialCode: "+372", name: "Estonia" },
  { code: "FI", dialCode: "+358", name: "Finland" },
  { code: "FR", dialCode: "+33",  name: "France" },
  { code: "GE", dialCode: "+995", name: "Georgia" },
  { code: "DE", dialCode: "+49",  name: "Germany" },
  { code: "GR", dialCode: "+30",  name: "Greece" },
  { code: "HU", dialCode: "+36",  name: "Hungary" },
  { code: "IS", dialCode: "+354", name: "Iceland" },
  { code: "IN", dialCode: "+91",  name: "India" },
  { code: "IE", dialCode: "+353", name: "Ireland" },
  { code: "IL", dialCode: "+972", name: "Israel" },
  { code: "IT", dialCode: "+39",  name: "Italy" },
  { code: "JP", dialCode: "+81",  name: "Japan" },
  { code: "LV", dialCode: "+371", name: "Latvia" },
  { code: "LI", dialCode: "+423", name: "Liechtenstein" },
  { code: "LT", dialCode: "+370", name: "Lithuania" },
  { code: "LU", dialCode: "+352", name: "Luxembourg" },
  { code: "MT", dialCode: "+356", name: "Malta" },
  { code: "MX", dialCode: "+52",  name: "Mexico" },
  { code: "MD", dialCode: "+373", name: "Moldova" },
  { code: "MC", dialCode: "+377", name: "Monaco" },
  { code: "ME", dialCode: "+382", name: "Montenegro" },
  { code: "NL", dialCode: "+31",  name: "Netherlands" },
  { code: "MK", dialCode: "+389", name: "North Macedonia" },
  { code: "NO", dialCode: "+47",  name: "Norway" },
  { code: "PL", dialCode: "+48",  name: "Poland" },
  { code: "PT", dialCode: "+351", name: "Portugal" },
  { code: "RO", dialCode: "+40",  name: "Romania" },
  { code: "SA", dialCode: "+966", name: "Saudi Arabia" },
  { code: "RS", dialCode: "+381", name: "Serbia" },
  { code: "SG", dialCode: "+65",  name: "Singapore" },
  { code: "SK", dialCode: "+421", name: "Slovakia" },
  { code: "SI", dialCode: "+386", name: "Slovenia" },
  { code: "KR", dialCode: "+82",  name: "South Korea" },
  { code: "ES", dialCode: "+34",  name: "Spain" },
  { code: "SE", dialCode: "+46",  name: "Sweden" },
  { code: "CH", dialCode: "+41",  name: "Switzerland" },
  { code: "TR", dialCode: "+90",  name: "Turkey" },
  { code: "UA", dialCode: "+380", name: "Ukraine" },
  { code: "AE", dialCode: "+971", name: "United Arab Emirates" },
  { code: "GB", dialCode: "+44",  name: "United Kingdom" },
  { code: "US", dialCode: "+1",   name: "United States" },
];

function countryCodeToFlag(code) {
  if (typeof code !== "string" || code.length !== 2) return "";
  const base = 0x1f1e6 - 65;
  return (
    String.fromCodePoint(code.charCodeAt(0) + base) +
    String.fromCodePoint(code.charCodeAt(1) + base)
  );
}

function parsePhoneDialCountry(phoneNumber, userCountry) {
  const fallback =
    userCountry && PHONE_DIAL_CODES.some((c) => c.code === userCountry)
      ? userCountry
      : "US";

  if (typeof phoneNumber !== "string" || !phoneNumber.startsWith("+")) {
    return fallback;
  }

  const matches = PHONE_DIAL_CODES.filter((c) => phoneNumber.startsWith(c.dialCode));
  if (!matches.length) return fallback;

  matches.sort((a, b) => b.dialCode.length - a.dialCode.length);
  const preferred = matches.find((c) => c.code === userCountry);
  return preferred ? preferred.code : matches[0].code;
}

class AccountPage extends React.Component {
  constructor() {
    super();
    this.state = {
      showAlert: false,
      alertType: "warning",
      alertContent: undefined,
      loading: null,
      avatarPreviewUrl: null,
      avatarUploadStatus: null, // { state: 'load'|'done'|'error', text: string }
      referralData: null,
      referralCodeInput: "",
      referralApplyStatus: null, // null | 'loading' | 'success' | 'error'
      referralApplyMessage: "",
      referralCopied: null, // null | 'code' | 'link'
      phoneVerifyCode: "",
      phoneVerifySubmitting: false,
      phoneVerifyError: "",
      optimisticPhoneVerification: null,
      terminalPhoneVerificationStatus: null,
      phoneInitiating: false,
      phoneInitiateError: "",
      phoneVerifySecondsLeft: null,
      phoneVerificationProvider: "twilio",
      phoneDialCountry: "",
      steamChecks: { loading: false, inUse: false, warnings: [] },
      emailVerifiedBanner: null, // 'success' | 'error_expired' | 'error_invalid' | null
      steamPendingEmail: null,   // pre-populated from backend on mount
      discordLinkBanner: null,   // null | 'success' | 'error_already_linked' | 'error_not_configured' | 'error_generic'
      discordUnlinking: false,
      emailPrefsSaving: false,
      emailPrefsError: "",
    };
  }

  componentDidMount() {
    this.fetchReferralData();
    this.refreshSessionUserBeforePendingReferral();
    if (this.props?.catalog?.user?.phoneVerification?.status === "code_sent") {
      this.startPhoneExpireTimer();
    }
    this.syncPhoneDialCountry();
    this.handleEmailVerifyQueryParam();
    this.handleDiscordQueryParam();
    this.fetchSteamPendingEmail();
  }

  handleEmailVerifyQueryParam = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("email_verified") === "1") {
        this.setState({ emailVerifiedBanner: "success" });
        params.delete("email_verified");
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${params.toString() ? `?${params}` : ""}`
        );
        // Refresh session so Redux user gets the real email.
        if (typeof this.props?.actions?.refreshSessionUser === "function") {
          this.props.actions.refreshSessionUser().catch(() => {});
        }
      } else if (params.has("email_verify_error")) {
        const reason = params.get("email_verify_error");
        this.setState({
          emailVerifiedBanner: reason === "expired" ? "error_expired" : "error_invalid",
        });
        params.delete("email_verify_error");
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${params.toString() ? `?${params}` : ""}`
        );
      }
    } catch (_) {
      // Best-effort URL handling.
    }
  };

  handleDiscordQueryParam = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("discord_linked") === "1") {
        this.setState({ discordLinkBanner: "success" });
        params.delete("discord_linked");
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${params.toString() ? `?${params}` : ""}`
        );
        if (typeof this.props?.actions?.refreshSessionUser === "function") {
          this.props.actions.refreshSessionUser().catch(() => {});
        }
      } else if (params.has("discord_error")) {
        const reason = params.get("discord_error");
        let banner = "error_generic";
        if (reason === "already_linked") banner = "error_already_linked";
        else if (reason === "not_configured") banner = "error_not_configured";
        this.setState({ discordLinkBanner: banner });
        params.delete("discord_error");
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${params.toString() ? `?${params}` : ""}`
        );
      }
    } catch (_) {
      // Best-effort URL handling.
    }
  };

  handleDiscordUnlink = async () => {
    this.setState({ discordUnlinking: true });
    try {
      await fetch(`${basePath}/api/v1/account/discord/unlink`, {
        method: "POST",
        credentials: "include",
      });
      if (typeof this.props?.actions?.refreshSessionUser === "function") {
        await this.props.actions.refreshSessionUser();
      }
    } catch (_) {
      // Best-effort unlink.
    } finally {
      this.setState({ discordUnlinking: false });
    }
  };

  // Preferences save immediately (independent of the main "Save Changes"
  // button) so toggling a switch has instant effect, similar to Discord
  // linking above.
  handleEmailNotificationPreferenceChange = async (key, checked) => {
    const user = this.props?.catalog?.user;
    if (!user?.userId) return;

    const currentPreferences = user.emailNotificationPreferences || {};
    const nextPreferences = {
      nonTransactionalOptOut: currentPreferences.nonTransactionalOptOut === true,
      queueOpenOptOut: currentPreferences.queueOpenOptOut === true,
      [key]: !checked,
    };

    this.setState({ emailPrefsSaving: true, emailPrefsError: "" });
    try {
      const res = await fetch(`${basePath}/api/v1/account`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          data: {
            userId: user.userId,
            emailNotificationPreferences: nextPreferences,
          },
        }),
      });
      const response = await res.json().catch(() => null);
      if (!res.ok || response?.ok === false) {
        throw new Error(
          response?.message || "Failed to update email preferences"
        );
      }
      if (typeof this.props?.actions?.refreshSessionUser === "function") {
        await this.props.actions.refreshSessionUser();
      }
    } catch (err) {
      this.setState({
        emailPrefsError:
          err?.message || "Failed to update email preferences. Please try again.",
      });
    } finally {
      this.setState({ emailPrefsSaving: false });
    }
  };

  fetchSteamPendingEmail = async () => {
    try {
      const user = this.props?.catalog?.user;
      if (!user?.emailIsSynthetic) return;

      const res = await fetch(`${basePath}/api/v1/account/email-verification-status`, {
        credentials: "include",
      });
      const data = await res.json();
      if (data?.ok && data?.pendingEmail) {
        this.setState({ steamPendingEmail: data.pendingEmail });
      }
    } catch (_) {
      // Best-effort prefetch only.
    }
  };

  refreshSessionUserBeforePendingReferral = async () => {
    const refreshSessionUser = this.props?.actions?.refreshSessionUser;
    if (typeof refreshSessionUser !== "function") {
      this.checkPendingReferral();
      return;
    }

    let refreshedUser = null;
    try {
      refreshedUser = await refreshSessionUser();
    } catch (_) {
      // Best-effort refresh only.
    } finally {
      this.checkPendingReferral(refreshedUser);
    }
  };

  checkPendingReferral = (authoritativeUser = null) => {
    try {
      const pendingCode = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (!pendingCode) return;

      const { catalog } = this.props;
      const user = authoritativeUser || catalog?.user;
      const merged = objectAssign({}, catalog?.user || {}, catalog?.account || {});
      const accountStatus =
        typeof authoritativeUser?.accountStatus === "string"
          ? authoritativeUser.accountStatus
          : merged.accountStatus || "pending";
      if (accountStatus === "verified") {
        window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
        return;
      }
      if (user?.referredByCode) {
        window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
        return;
      }

      this.setState({ referralCodeInput: pendingCode }, () => {
        this.handleApplyReferralCode();
      });
    } catch (_) {
      // Best-effort auto-apply only.
    }
  };

  fetchReferralData = () => {
    fetch(`${basePath}/api/v1/referral/my-code`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok) this.setState({ referralData: data });
      })
      .catch(() => {});
  };

  handleApplyReferralCode = async () => {
    const code = this.state.referralCodeInput.trim();
    if (!code) return;
    this.setState({ referralApplyStatus: "loading", referralApplyMessage: "" });
    try {
      const res = await fetch(`${basePath}/api/v1/referral/apply`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data?.ok) {
        try {
          window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
        } catch (_) {}
        this.setState({
          referralApplyStatus: "success",
          referralApplyMessage: data.referrerNickname
            ? `Referred by ${data.referrerNickname}! Buy any package to get 1 extra free month.`
            : "Referral code applied! Buy any package to get 1 extra free month.",
        });
      } else {
        this.setState({
          referralApplyStatus: "error",
          referralApplyMessage: data?.message || "Invalid referral code",
        });
      }
    } catch {
      this.setState({ referralApplyStatus: "error", referralApplyMessage: "Network error" });
    }
  };

  handleCopyReferral = (text, which) => {
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ referralCopied: which });
      setTimeout(() => this.setState({ referralCopied: null }), 2000);
    }).catch(() => {});
  };

  componentDidUpdate(prevProps) {
    if (
      shouldResetAvatarPreview({
        hasAvatarPreviewUrl: !!this.state.avatarPreviewUrl,
        basePath,
        previousUser: prevProps?.catalog?.user,
        nextUser: this.props?.catalog?.user,
        previousAvatarCacheBust: prevProps?.catalog?.avatarCacheBust,
        nextAvatarCacheBust: this.props?.catalog?.avatarCacheBust,
      })
    ) {
      URL.revokeObjectURL(this.state.avatarPreviewUrl);
      this.setState({ avatarPreviewUrl: null });
    }

    const prevPhoneVerification = prevProps?.catalog?.user?.phoneVerification || null;
    const nextPhoneVerification = this.props?.catalog?.user?.phoneVerification || null;
    const prevPhoneVerificationKey = JSON.stringify(prevPhoneVerification);
    const nextPhoneVerificationKey = JSON.stringify(nextPhoneVerification);

    if (prevPhoneVerificationKey !== nextPhoneVerificationKey) {
      const nextState = {
        phoneVerifyCode: "",
        phoneVerifyError: "",
        phoneVerifySubmitting: false,
        phoneInitiating: false,
        phoneInitiateError: "",
      };

      if (
        nextPhoneVerification?.provider === "didit" ||
        nextPhoneVerification?.provider === "twilio"
      ) {
        nextState.phoneVerificationProvider = nextPhoneVerification.provider;
      }

      if (this.state.optimisticPhoneVerification) {
        nextState.optimisticPhoneVerification = null;
      }

      if (this.state.terminalPhoneVerificationStatus) {
        nextState.terminalPhoneVerificationStatus = null;
      }

      if (Object.keys(nextState).length > 0) {
        this.setState(nextState);
      }

      if (nextPhoneVerification?.status === "code_sent") {
        this.startPhoneExpireTimer();
      } else {
        this.clearPhoneExpireTimer();
      }
    }

    const prevStoredPhone = prevProps?.catalog?.user?.phoneNumber || "";
    const nextStoredPhone = this.props?.catalog?.user?.phoneNumber || "";
    if (prevStoredPhone !== nextStoredPhone) {
      this.syncPhoneDialCountry();
    }
  }

  componentWillUnmount() {
    if (this.state.avatarPreviewUrl) {
      URL.revokeObjectURL(this.state.avatarPreviewUrl);
    }
    this.clearPhoneExpireTimer();
  }

  startPhoneExpireTimer() {
    this.clearPhoneExpireTimer();
    const expiresAt = this.props?.catalog?.user?.phoneVerification?.expiresAt;
    if (!expiresAt) return;
    const expiresMs = new Date(expiresAt).getTime();
    if (Number.isNaN(expiresMs)) return;

    const tick = () => {
      const secondsLeft = Math.max(0, Math.ceil((expiresMs - Date.now()) / 1000));
      this.setState({ phoneVerifySecondsLeft: secondsLeft });
      if (secondsLeft <= 0) {
        this.clearPhoneExpireTimer();
        this.setState({ terminalPhoneVerificationStatus: "expired" });
      }
    };

    tick();
    this._phoneExpireInterval = setInterval(tick, 1000);
  }

  clearPhoneExpireTimer() {
    if (this._phoneExpireInterval) {
      clearInterval(this._phoneExpireInterval);
      this._phoneExpireInterval = null;
    }
  }

  syncPhoneDialCountry() {
    const phone = this.props?.catalog?.user?.phoneNumber || "";
    const country = this.props?.catalog?.user?.country || "";
    this.setState({ phoneDialCountry: parsePhoneDialCountry(phone, country) });
  }

  handlePhoneDialCodeChange = (newCountryCode) => {
    const prevEntry = PHONE_DIAL_CODES.find(
      (c) => c.code === this.state.phoneDialCountry
    );
    const nextEntry = PHONE_DIAL_CODES.find((c) => c.code === newCountryCode);
    if (!nextEntry) return;

    const prevDialCode = prevEntry?.dialCode || "";
    const currentFull =
      typeof this.props?.catalog?.account?.phoneNumber === "string"
        ? this.props.catalog.account.phoneNumber
        : typeof this.props?.catalog?.user?.phoneNumber === "string"
          ? this.props.catalog.user.phoneNumber
          : "";

    const localPart = currentFull.startsWith(prevDialCode)
      ? currentFull.slice(prevDialCode.length)
      : currentFull;

    this.setState({ phoneDialCountry: newCountryCode });
    this.props.actions.updateAccountFormField({
      name: "phoneNumber",
      value: nextEntry.dialCode + localPart,
    });
  };

  handlePhoneLocalChange = (e) => {
    const entry = PHONE_DIAL_CODES.find(
      (c) => c.code === this.state.phoneDialCountry
    );
    const dialCode = entry?.dialCode || "";
    this.props.actions.updateAccountFormField({
      name: "phoneNumber",
      value: dialCode + e.target.value,
    });
  };

  handleFormChange = (e) => {
    if (e.target.name === "steamId64") {
      this.setState({
        steamChecks: { loading: false, inUse: false, warnings: [] },
      });
    }

    if (e.target.name === "dateOfBirth") {
      this.handleDateOfBirthChange(e.target.value);
    } else {
      this.props.actions.updateAccountFormField({
        name: e.target.name,
        value: e.target.value,
      });
    }
  };

  handleSteamId64Blur = async () => {
    const { catalog } = this.props;
    const steamId64 = String(
      catalog?.account?.steamId64 ?? catalog?.user?.steamId64 ?? ""
    ).trim();
    if (!steamId64 || !/^\d{15,20}$/.test(steamId64)) {
      this.setState({
        steamChecks: { loading: false, inUse: false, warnings: [] },
      });
      return;
    }
    this.setState({ steamChecks: { loading: true, inUse: false, warnings: [] } });
    try {
      const result = await fetchSteamChecks(steamId64);
      this.setState({
        steamChecks: {
          loading: false,
          inUse: result?.checks?.inUse ?? false,
          warnings: result?.checks?.warnings ?? [],
        },
      });
    } catch (_) {
      this.setState({ steamChecks: { loading: false, inUse: false, warnings: [] } });
    }
  };

  handleDateOfBirthChange = (value) => {
    const dob = formatDateOfBirthForSubmit(value);
    this.props.actions.updateAccountFormField({
      name: "dateOfBirth",
      value: dob,
    });

    const dobAge = calculateAgeFromDobValue(dob);
    this.props.actions.updateAccountFormField({
      name: "age",
      value: dobAge !== null ? String(dobAge) : "",
    });
  };

  handleFileFieldChange = ({ file, name, tmpFilename }) => {
    if (name === "avatar" && file) {
      if (this.state.avatarPreviewUrl) {
        URL.revokeObjectURL(this.state.avatarPreviewUrl);
      }
      const avatarPreviewUrl = URL.createObjectURL(file);
      this.setState({
        avatarPreviewUrl,
      });
    }

    this.props.actions.updateAccountFormField({
      name,
      value: {
        file: file.name,
        tmpFilename,
      },
    });
  };

  handleUploadStatusChange = ({ name, state, text }) => {
    if (name === "avatar") {
      this.setState({ avatarUploadStatus: { state, text } });
    }
  };

  isPendingReviewPhoneCorrectionMode = () =>
    accountPendingReviewPhoneCorrectionMode(this.props?.catalog?.user || {});

  buildPendingReviewAccountPatch = ({
    user,
    account,
    normalizedDateOfBirth,
    dobAge,
  }) => {
    const data = { userId: user.userId };

    for (const fieldName of PENDING_REVIEW_PROFILE_EDITABLE_FIELDS) {
      if (Object.prototype.hasOwnProperty.call(account, fieldName)) {
        data[fieldName] = account[fieldName];
      }
    }

    if (Object.prototype.hasOwnProperty.call(data, "dateOfBirth")) {
      data.dateOfBirth = normalizedDateOfBirth;
      data.age = dobAge;
    }

    return data;
  };

  saveChanges = () => {
    const { user, account } = this.props.catalog;

    const merged = objectAssign({}, user, account);
    const pendingReviewPhoneCorrectionMode =
      this.isPendingReviewPhoneCorrectionMode();
    const isPendingReviewProfileEditMode =
      user?.accountStatus === "pending_review";
    const validatePendingReviewPhone =
      isPendingReviewProfileEditMode &&
      Object.prototype.hasOwnProperty.call(account, "phoneNumber");
    const validationError = validatePendingReviewPhone
      ? getInternationalPhoneValidationError(merged.phoneNumber)
      : isPendingReviewProfileEditMode
        ? null
        : getAccountSaveValidationError(merged);
    if (validationError) {
      this.props.actions.setAccountUpdateAlert(validationError);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    const { dateOfBirth = "" } = merged;
    const normalizedDateOfBirth = normalizeDateOfBirthValue(dateOfBirth);
    const dobAge = calculateAgeFromDobValue(normalizedDateOfBirth);

    const data = isPendingReviewProfileEditMode
      ? this.buildPendingReviewAccountPatch({
          user,
          account,
          normalizedDateOfBirth,
          dobAge,
        })
      : objectAssign({}, account, { userId: user.userId });
    if (!isPendingReviewProfileEditMode) {
      data.dateOfBirth = normalizedDateOfBirth;
      data.age = dobAge;
    }

    this.props.actions.submitAccountChanges(data);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 24);
  };

  dismissSuccessAlert = () => {
    this.props.actions.dismissSuccessAccountAlert();
  };

  dismissAlert = () => {
    this.props.actions.dismissAccountUpdateAlert();
  };

  handlePhoneVerifyCodeChange = (e) => {
    const raw = String(e.target.value || "").replace(/\D/g, "").slice(0, 6);
    this.setState({ phoneVerifyCode: raw, phoneVerifyError: "" });
  };

  saveDraftPhoneNumberBeforeVerification = async () => {
    const user = this.props?.catalog?.user;
    const account = this.props?.catalog?.account || {};
    if (!user?.userId) return true;
    if (!Object.prototype.hasOwnProperty.call(account, "phoneNumber")) {
      return true;
    }

    const draftPhoneNumber =
      typeof account.phoneNumber === "string" ? account.phoneNumber : "";
    const storedPhoneNumber =
      typeof user.phoneNumber === "string" ? user.phoneNumber : "";
    if (
      normalizePhoneForComparison(draftPhoneNumber) ===
      normalizePhoneForComparison(storedPhoneNumber)
    ) {
      return true;
    }

    const validationError = getInternationalPhoneValidationError(draftPhoneNumber);
    if (validationError) {
      this.setState({ phoneInitiateError: validationError });
      return false;
    }

    try {
      const res = await fetch(`${basePath}/api/v1/account`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          data: {
            userId: user.userId,
            phoneNumber: draftPhoneNumber,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(
          data?.message || "Failed to save your phone number. Please try again."
        );
      }
      return true;
    } catch (err) {
      this.setState({
        phoneInitiateError:
          err?.message || "Failed to save your phone number. Please try again.",
      });
      return false;
    }
  };

  handleInitiatePhoneVerification = async () => {
    this.setState({ phoneInitiating: true, phoneInitiateError: "" });

    try {
      const phoneSaved = await this.saveDraftPhoneNumberBeforeVerification();
      if (!phoneSaved) {
        this.setState({ phoneInitiating: false });
        return;
      }

      const res = await fetch(
        `${basePath}/api/v1/account/initiate-phone-verification`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            provider:
              DIDIT_PHONE_VERIFICATION_UI_ENABLED &&
              this.state.phoneVerificationProvider === "didit"
                ? "didit"
                : "twilio",
          }),
        }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        const errorMessage =
          (typeof data?.message === "string" && data.message) ||
          "Failed to send verification code. Please try again.";
        const hasExistingActiveCode =
          isActivePhoneVerificationMessage(errorMessage);

        this.setState({
          phoneInitiating: false,
          phoneInitiateError: hasExistingActiveCode ? "" : errorMessage,
          terminalPhoneVerificationStatus: hasExistingActiveCode
            ? "code_sent"
            : this.state.terminalPhoneVerificationStatus,
        });

        if (
          hasExistingActiveCode &&
          typeof this.props?.actions?.refreshSessionUser === "function"
        ) {
          try {
            await this.props.actions.refreshSessionUser();
          } catch (_) {
            // Keep the local code_sent state so the OTP panel still opens.
          }
        }
        return;
      }

      this.setState({
        phoneInitiating: false,
        phoneInitiateError: "",
        terminalPhoneVerificationStatus: null,
      });

      if (typeof this.props?.actions?.refreshSessionUser === "function") {
        try {
          await this.props.actions.refreshSessionUser();
        } catch (_) {
          // Best-effort refresh; the OTP panel will appear on next successful refresh.
        }
      }
    } catch (_) {
      this.setState({
        phoneInitiating: false,
        phoneInitiateError: "Network error. Please try again.",
      });
    }
  };

  handleTryAlternatePhoneProvider = (provider) => {
    if (!DIDIT_PHONE_VERIFICATION_UI_ENABLED) return;
    if (provider !== "twilio" && provider !== "didit") return;
    this.setState({ phoneVerificationProvider: provider }, () => {
      this.handleInitiatePhoneVerification();
    });
  };

  handleVerifyPhoneSubmit = async () => {
    const digits = String(this.state.phoneVerifyCode || "").replace(/\D/g, "");
    if (digits.length !== 6) {
      this.setState({
        phoneVerifyError: "Enter the 6-digit code from your text message.",
      });
      return;
    }

    this.setState({ phoneVerifySubmitting: true, phoneVerifyError: "" });

    try {
      const res = await fetch(`${basePath}/api/v1/account/verify-phone`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: digits }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        const errorMessage =
          (typeof data?.message === "string" && data.message) ||
          "Verification failed. Please try again.";
        const terminalPhoneVerificationStatus =
          getTerminalPhoneVerificationStatus(errorMessage);

        this.setState({
          phoneVerifySubmitting: false,
          phoneVerifyCode: terminalPhoneVerificationStatus
            ? ""
            : this.state.phoneVerifyCode,
          phoneVerifyError: errorMessage,
          terminalPhoneVerificationStatus,
        });

        if (
          terminalPhoneVerificationStatus &&
          typeof this.props?.actions?.refreshSessionUser === "function"
        ) {
          try {
            await this.props.actions.refreshSessionUser();
          } catch (_) {
            // Best-effort refresh only. Local terminal state already collapsed the flow.
          }
        }
        return;
      }

      this.setState({
        phoneVerifySubmitting: false,
        phoneVerifyCode: "",
        phoneVerifyError: "",
        terminalPhoneVerificationStatus: null,
        optimisticPhoneVerification:
          data?.phoneVerification && typeof data.phoneVerification === "object"
            ? data.phoneVerification
            : { status: "verified" },
      });

      if (typeof this.props?.actions?.refreshSessionUser === "function") {
        try {
          await this.props.actions.refreshSessionUser();
        } catch (_) {
          // Keep the optimistic verified state until the next authoritative refresh.
        }
      }
    } catch (_) {
      this.setState({
        phoneVerifySubmitting: false,
        phoneVerifyError: "Network error. Please try again.",
      });
    }
  };

  validateForm = (e) => {
    e.preventDefault();
  };

  render() {
    const { classes, catalog } = this.props;
    const {
      user,
      account,
      accountUpdateComplete = false,
      accountUpdateCompleteResult = null,
      accountUpdateDuring = false,
      accountUpdateAlert,
      accountUpdateAlertText,
    } = catalog;

    const storedPhoneNumber =
      typeof user?.phoneNumber === "string" ? user.phoneNumber : "";
    const serverAccountStatus =
      typeof user?.accountStatus === "string" ? user.accountStatus : "pending";
    const isVerifiedCompletionMode = serverAccountStatus === "verified";
    const isPendingReviewProfileEditMode =
      serverAccountStatus === "pending_review";
    const isPendingReviewPhoneCorrectionMode =
      accountPendingReviewPhoneCorrectionMode(user);
    const isLocked =
      serverAccountStatus === "verified" ||
      (serverAccountStatus === "pending_review" &&
        !isPendingReviewPhoneCorrectionMode);
    const canEditAllFields = !isLocked && !isPendingReviewPhoneCorrectionMode;
    const mergedFormSource = objectAssign({}, user, account);
    const storedFormSource = objectAssign({}, user);

    const canEditVerifiedName = isVerifiedCompletionMode;
    const canEditVerifiedNickname = isVerifiedCompletionMode;
    const canEditVerifiedDateOfBirth = isVerifiedCompletionMode;
    const canEditVerifiedCountry = isVerifiedCompletionMode;
    const canEditVerifiedPhone = isVerifiedCompletionMode;
    const canEditVerifiedSteamId64 =
      isVerifiedCompletionMode &&
      !String(storedFormSource?.steamId64 || "").trim();
    const canEditVerifiedSteamProfileUrl =
      isVerifiedCompletionMode &&
      !String(storedFormSource?.steamProfileUrl || "").trim();
    const canEditVerifiedFaceitProfileUrl = isVerifiedCompletionMode;
    const canEditVerifiedSteamInventory = isVerifiedCompletionMode;
    const canEditVerifiedAvatar = isVerifiedCompletionMode;
    const canEditVerifiedGender = isVerifiedCompletionMode;
    const canEditVerifiedShowGenderOnProfile = isVerifiedCompletionMode;

    const canEditName =
      canEditAllFields || canEditVerifiedName || isPendingReviewProfileEditMode;
    const canEditNickname =
      canEditAllFields ||
      canEditVerifiedNickname ||
      isPendingReviewProfileEditMode;
    const canEditDateOfBirth =
      canEditAllFields ||
      canEditVerifiedDateOfBirth ||
      isPendingReviewProfileEditMode;
    const canEditCountry = canEditAllFields || canEditVerifiedCountry;
    const canEditPhone =
      canEditAllFields ||
      isPendingReviewProfileEditMode ||
      isPendingReviewPhoneCorrectionMode ||
      canEditVerifiedPhone;
    const canEditSteamId64 = canEditAllFields || canEditVerifiedSteamId64;
    const canEditSteamProfileUrl =
      canEditAllFields || canEditVerifiedSteamProfileUrl;
    const canEditFaceitProfileUrl =
      canEditAllFields || canEditVerifiedFaceitProfileUrl;
    const canEditSteamInventory =
      canEditAllFields ||
      canEditVerifiedSteamInventory ||
      isPendingReviewProfileEditMode;
    const canEditAvatar =
      canEditAllFields ||
      canEditVerifiedAvatar ||
      isPendingReviewProfileEditMode;
    const canEditGender =
      canEditAllFields ||
      canEditVerifiedGender ||
      isPendingReviewProfileEditMode;
    const canEditShowGenderOnProfile =
      canEditAllFields ||
      canEditVerifiedShowGenderOnProfile ||
      isPendingReviewProfileEditMode;
    const canSaveChanges =
      canEditAllFields ||
      isPendingReviewProfileEditMode ||
      isPendingReviewPhoneCorrectionMode ||
      canEditVerifiedName ||
      canEditVerifiedNickname ||
      canEditVerifiedDateOfBirth ||
      canEditVerifiedCountry ||
      canEditVerifiedPhone ||
      canEditVerifiedSteamId64 ||
      canEditVerifiedSteamProfileUrl ||
      canEditVerifiedFaceitProfileUrl ||
      canEditVerifiedSteamInventory ||
      canEditVerifiedAvatar ||
      canEditVerifiedGender ||
      canEditVerifiedShowGenderOnProfile;
    const pickFieldValue = (canEditField, fieldName, fallback = "") => {
      const source = canEditField ? mergedFormSource : storedFormSource;
      const value = source?.[fieldName];
      return value == null ? fallback : value;
    };

    const name = pickFieldValue(canEditName, "name", "");
    const nickname = pickFieldValue(canEditNickname, "nickname", "");
    const age = pickFieldValue(false, "age", "");
    const country = pickFieldValue(canEditCountry, "country", "");
    const accountDisplayStatus = pickFieldValue(
      false,
      "accountDisplayStatus",
      "PENDING VERIFICATION"
    );
    const steamId64 =
      pickFieldValue(canEditSteamId64, "steamId64", "") ||
      pickFieldValue(canEditSteamId64, "steam64Id", "");
    const steamProfileUrl =
      pickFieldValue(canEditSteamProfileUrl, "steamProfileUrl", "") ||
      storedFormSource?.steamProvider?.profileUrl ||
      "";
    const rawPhoneNumber = pickFieldValue(canEditPhone, "phoneNumber", "");
    const faceitProfileUrl = pickFieldValue(
      canEditFaceitProfileUrl,
      "faceitProfileUrl",
      ""
    );
    const steamInventoryValue = pickFieldValue(
      canEditSteamInventory,
      "steamInventoryValue",
      canEditSteamInventory ? "" : "none"
    );
    const dateOfBirth = pickFieldValue(canEditDateOfBirth, "dateOfBirth", "");
    const gender = pickFieldValue(canEditGender, "gender", "");
    const showGenderOnProfile = pickFieldValue(
      canEditShowGenderOnProfile,
      "showGenderOnProfile",
      false
    );

    // Preferences default to opted-in (enabled) when unset, matching backend
    // semantics: absence of an opt-out flag means the member hasn't opted out.
    const emailNotificationPreferences = user?.emailNotificationPreferences || {};
    const emailNotificationsEnabled =
      emailNotificationPreferences.nonTransactionalOptOut !== true;
    const queueOpenEmailsEnabled =
      emailNotificationPreferences.queueOpenOptOut !== true;

    const storedFaceitProfileUrl = String(storedFormSource?.faceitProfileUrl || "").trim();
    const faceitWillTriggerReview =
      isVerifiedCompletionMode &&
      !!storedFaceitProfileUrl &&
      faceitProfileUrl.trim() !== storedFaceitProfileUrl;

    const accountStatus = serverAccountStatus;

    const draftPhoneNumber =
      typeof rawPhoneNumber === "string" ? rawPhoneNumber : "";
    const phoneNumber = canEditPhone ? draftPhoneNumber : storedPhoneNumber;
    const displayedPhoneMatchesStoredPhone =
      normalizePhoneForComparison(phoneNumber) ===
      normalizePhoneForComparison(storedPhoneNumber);
    const serverPhoneVerification = user?.phoneVerification || null;
    const terminalPhoneVerificationStatus =
      this.state.terminalPhoneVerificationStatus;
    const serverOtpChallengeActive =
      serverPhoneVerification?.status === "code_sent";
    const effectivePhoneVerification = terminalPhoneVerificationStatus
      ? { status: terminalPhoneVerificationStatus }
      : serverOtpChallengeActive
      ? serverPhoneVerification
      : displayedPhoneMatchesStoredPhone
        ? this.state.optimisticPhoneVerification || serverPhoneVerification
        : null;
    const phoneVerificationStatus = effectivePhoneVerification?.status;
    const activePhoneVerificationProvider =
      effectivePhoneVerification?.provider === "didit" ? "didit" : "twilio";
    const selectedPhoneVerificationProvider =
      this.state.phoneVerificationProvider === "didit" ? "didit" : "twilio";
    const canSendPhoneVerificationCode =
      phoneVerificationStatus === "phone_verification_ready" ||
      phoneVerificationStatus === "expired" ||
      ((accountStatus === "verified" || accountStatus === "active") &&
        phoneVerificationStatus !== "verified" &&
        phoneVerificationStatus !== "code_sent");
    const canResendWithSelectedProvider =
      DIDIT_PHONE_VERIFICATION_UI_ENABLED &&
      phoneVerificationStatus === "code_sent" &&
      selectedPhoneVerificationProvider !== activePhoneVerificationProvider;
    const showSendPhoneVerificationCode =
      Boolean(user) &&
      (canSendPhoneVerificationCode || canResendWithSelectedProvider);
    const showPhoneVerificationProviderSwitch =
      DIDIT_PHONE_VERIFICATION_UI_ENABLED &&
      Boolean(user) &&
      (canSendPhoneVerificationCode || phoneVerificationStatus === "code_sent");
    const dialCountry = this.state.phoneDialCountry || "US";
    const dialEntry =
      PHONE_DIAL_CODES.find((c) => c.code === dialCountry) ||
      PHONE_DIAL_CODES.find((c) => c.code === "US");
    const dialCode = dialEntry?.dialCode || "";
    const localPhoneNumber = phoneNumber.startsWith(dialCode)
      ? phoneNumber.slice(dialCode.length)
      : phoneNumber;
    const avatarUserId = user && user.userId ? user.userId : null;
    const avatarVersion = pickAvatarUpdatedAtMs(user, catalog?.avatarCacheBust);
    const stableAvatarSrc = resolveAvatarSrc({
      basePath,
      picture: user?.picture,
      userId: avatarUserId || user?._id,
      avatarUpdatedAtMs: avatarVersion,
      hasCustomAvatar: !!user?.avatar,
    });
    const avatarSrc =
      this.state.avatarPreviewUrl ||
      stableAvatarSrc ||
      user?.picture ||
      undefined;
    const normalizedDateOfBirth = normalizeDateOfBirthValue(dateOfBirth);
    const dobAge = calculateAgeFromDobValue(normalizedDateOfBirth);
    const effectiveAge = dobAge !== null ? dobAge : age;
    const dobPickerValue = normalizedDateOfBirth
      ? (() => {
          const [year, month, day] = normalizedDateOfBirth
            .split("-")
            .map((part) => Number(part));
          return new Date(year, month - 1, day);
        })()
      : null;

    return (
      <>
        <PageShell maxWidth="lg">
          <div className={classes.homepageRoot}>
            <div className={classes.contentWidth}>
              {!accountUpdateAlert && accountUpdateDuring && (
                <Alert
                  icon={
                    <CircularProgress
                      className={classes.loadingAnim}
                      style={{ width: 20, height: 20 }}
                    />
                  }
                  className={classes.alert}
                  severity="info"
                >
                  Please wait, saving your changes..
                </Alert>
              )}

              {!accountUpdateAlert &&
                !accountUpdateDuring &&
                accountUpdateComplete && (
                  <Alert
                    className={classes.alert}
                    severity="success"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={this.dismissSuccessAlert}
                      >
                        Dismiss
                      </Button>
                    }
                  >
                    Details updated successfully!
                  </Alert>
                )}

              {!accountUpdateComplete && accountUpdateAlert && (
                <Alert
                  className={classes.alert}
                  severity={this.state.alertType}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={this.dismissAlert}
                    >
                      Dismiss
                    </Button>
                  }
                >
                  <AlertTitle>Some fields are missing!</AlertTitle>
                  {accountUpdateAlertText}
                </Alert>
              )}

              {/* ── Steam email-verified confirmation / error banners ── */}
              {this.state.emailVerifiedBanner === "success" && (
                <Alert
                  className={classes.alert}
                  severity="success"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => this.setState({ emailVerifiedBanner: null })}
                    >
                      Dismiss
                    </Button>
                  }
                >
                  <AlertTitle>Email verified!</AlertTitle>
                  Your email address has been confirmed. You can now continue setting
                  up your profile.
                </Alert>
              )}
              {this.state.emailVerifiedBanner === "error_expired" && (
                <Alert
                  className={classes.alert}
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => this.setState({ emailVerifiedBanner: null })}
                    >
                      Dismiss
                    </Button>
                  }
                >
                  <AlertTitle>Verification link expired</AlertTitle>
                  The link is more than 24 hours old. Please scroll down to the Steam
                  profile completion section and request a new one.
                </Alert>
              )}
              {this.state.emailVerifiedBanner === "error_invalid" && (
                <Alert
                  className={classes.alert}
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => this.setState({ emailVerifiedBanner: null })}
                    >
                      Dismiss
                    </Button>
                  }
                >
                  <AlertTitle>Invalid verification link</AlertTitle>
                  This link has already been used or is not valid. If you are still
                  waiting to verify your email, please request a new link below.
                </Alert>
              )}

              {/* ── Discord link confirmation / error banners ── */}
              {this.state.discordLinkBanner === "success" && (
                <Alert
                  className={classes.alert}
                  severity="success"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => this.setState({ discordLinkBanner: null })}
                    >
                      Dismiss
                    </Button>
                  }
                >
                  <AlertTitle>Discord connected!</AlertTitle>
                  Your Discord account has been linked successfully.
                </Alert>
              )}
              {this.state.discordLinkBanner === "error_already_linked" && (
                <Alert
                  className={classes.alert}
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => this.setState({ discordLinkBanner: null })}
                    >
                      Dismiss
                    </Button>
                  }
                >
                  <AlertTitle>Discord link failed</AlertTitle>
                  That Discord account is already linked to another Ouro account.
                </Alert>
              )}
              {this.state.discordLinkBanner === "error_not_configured" && (
                <Alert
                  className={classes.alert}
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => this.setState({ discordLinkBanner: null })}
                    >
                      Dismiss
                    </Button>
                  }
                >
                  <AlertTitle>Discord link unavailable</AlertTitle>
                  Discord linking is not available right now.
                </Alert>
              )}
              {this.state.discordLinkBanner === "error_generic" && (
                <Alert
                  className={classes.alert}
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => this.setState({ discordLinkBanner: null })}
                    >
                      Dismiss
                    </Button>
                  }
                >
                  <AlertTitle>Discord link failed</AlertTitle>
                  Could not connect Discord. Please try again.
                </Alert>
              )}

              <ApplicationStatusBanner user={user} accountStatus={accountStatus} />

              {/* ── Steam completion card (name + email) ── */}
              {user?.signupSource === "steam" && (
                <SteamProfileCompletion
                  user={user}
                  initialPendingEmail={this.state.steamPendingEmail}
                />
              )}

              {/* ── Optional browser push for email-less Steam sign-ins ── */}
              <PushNotificationOptIn user={user} />

              {accountStatus !== "verified" && (
                <ReadinessChecklist
                  accountStatus={accountStatus}
                  phoneVerificationStatus={
                    user?.phoneVerification?.status ?? null
                  }
                  refreshToken={accountUpdateCompleteResult}
                />
              )}

              <Paper className={classes.lobbyFrame}>
                {!user && <Typography>Please wait...</Typography>}

                {user && (
                  <>
                    <div className={classes.accountInner}>
                      <MembershipSection />

                      <Typography variant="h5" sx={{ mb: 2 }}>
                        Account Settings
                      </Typography>

                      <ProfileTrustFactorBar
                        user={user}
                        accountStatus={accountStatus}
                        accountDisplayStatus={accountDisplayStatus}
                      />

                      {user.applicationCommunication && (
                        <ApplicationFeedbackBanner
                          applicationCommunication={user.applicationCommunication}
                        />
                      )}

                      {accountStatus === "noteligible" && (
                        <EligibilityAppealSection user={user} />
                      )}

                      {canEditAllFields && (
                        <Box
                          className={classes.verificationBorder}
                          sx={{ mb: 2 }}
                        >
                          <Typography
                            className={classes.verificationTitle}
                            component="h2"
                            sx={{ mb: 1 }}
                          >
                            {ACCOUNT_VERIFICATION_GUIDANCE_TITLE}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={classes.selfieExplanation}
                          >
                            {ACCOUNT_VERIFICATION_GUIDANCE_INTRO}
                          </Typography>
                          <Box
                            component="ul"
                            sx={{ m: 0, pl: 2.5, mt: 1.5, mb: 0 }}
                          >
                            {ACCOUNT_VERIFICATION_GUIDANCE_ITEMS.map(
                              (item) => (
                                <Typography
                                  key={item}
                                  component="li"
                                  variant="body2"
                                  className={classes.selfieExplanation}
                                  sx={{ mb: 0.5 }}
                                >
                                  {item}
                                </Typography>
                              )
                            )}
                          </Box>
                        </Box>
                      )}

                      <form
                        className={classes.root}
                        noValidate
                        autoComplete="off"
                      >
                        <Grid
                          container
                          rowSpacing={2}
                          columnSpacing={{ xs: 2, md: 3 }}
                          sx={{ px: { xs: 0, sm: 1 } }}
                        >
                          <TierSection
                            tier={1}
                            title="Tier 1: Get Started"
                            subtitle="Bare minimum needed to send your application to human review."
                          >
                          {/* Top row: 4 basics (matches prod layout) */}
                          <Grid size={{ xs: 12, md: 3 }}>
                            <TextField
                              fullWidth
                              disabled={!canEditNickname}
                              error={Boolean(getNicknameValidationError(nickname))}
                              helperText={NICKNAME_HINT}
                              id="account-username"
                              name="nickname"
                              label="Nickname"
                              variant="outlined"
                              onChange={this.handleFormChange}
                              value={nickname}
                              inputProps={{ maxLength: NICKNAME_MAX_LENGTH }}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              error={canEditSteamId64 && steamId64 === ""}
                              disabled={!canEditSteamId64}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              id="account-steam64-id"
                              label="SteamID 64"
                              name="steamId64"
                              onChange={this.handleFormChange}
                              onBlur={this.handleSteamId64Blur}
                              variant="outlined"
                              value={steamId64}
                              helperText="Steam users get this filled automatically."
                            />
                          </Grid>
                          {this.state.steamChecks.loading && (
                            <Grid size={{ xs: 12 }}>
                              <CircularProgress size={16} sx={{ mr: 1, verticalAlign: "middle" }} />
                              <Typography variant="caption" component="span">
                                Checking Steam profile...
                              </Typography>
                            </Grid>
                          )}
                          {!this.state.steamChecks.loading && this.state.steamChecks.inUse && (
                            <Grid size={{ xs: 12 }}>
                              <Alert severity="error">
                                This Steam ID is already linked to another Ouro account.
                              </Alert>
                            </Grid>
                          )}
                          {!this.state.steamChecks.loading &&
                            !this.state.steamChecks.inUse &&
                            this.state.steamChecks.warnings.includes("vac_banned") && (
                            <Grid size={{ xs: 12 }}>
                              <Alert severity="error">
                                Valve reports this Steam account has a VAC ban. VAC banned
                                accounts cannot join Ouro matchmaking.
                              </Alert>
                            </Grid>
                          )}
                          {!this.state.steamChecks.loading &&
                            !this.state.steamChecks.inUse &&
                            hasUnlikelySteamSignupWarnings(this.state.steamChecks.warnings) && (
                            <Grid size={{ xs: 12 }}>
                              <Alert severity="error">
                                <AlertTitle>
                                  {getUnlikelyAcceptanceCopy(this.state.steamChecks.warnings).title}
                                </AlertTitle>
                                {getUnlikelyAcceptanceCopy(this.state.steamChecks.warnings).body}
                              </Alert>
                            </Grid>
                          )}
                          {!this.state.steamChecks.loading &&
                            !this.state.steamChecks.inUse &&
                            hasHiddenSteamGameDetails(this.state.steamChecks.warnings) && (
                            <Grid size={{ xs: 12 }}>
                              <Alert severity="warning">
                                We cannot see this Steam account&apos;s CS2 hours. Set your
                                profile and Game details to Public, uncheck &quot;Always keep
                                my total playtime private&quot;, then open your{" "}
                                <a
                                  href={STEAM_PRIVACY_SETTINGS_URL}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Steam profile
                                </a>{" "}
                                in an incognito window and confirm CS2 is in the games list
                                with hours visible. Applications we cannot verify are highly
                                unlikely to be accepted.
                              </Alert>
                            </Grid>
                          )}
                          {!this.state.steamChecks.loading &&
                            !this.state.steamChecks.inUse &&
                            this.state.steamChecks.warnings.includes(
                              "friends_list_not_accessible"
                            ) &&
                            !hasHiddenSteamGameDetails(this.state.steamChecks.warnings) && (
                            <Grid size={{ xs: 12 }}>
                              <Alert severity="warning">
                                It looks like your Steam friends list may be private. You can
                                still submit your application, but we recommend verifying your{" "}
                                <a
                                  href={STEAM_PRIVACY_SETTINGS_URL}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Steam privacy settings
                                </a>{" "}
                                before doing so.
                              </Alert>
                            </Grid>
                          )}

                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              disabled={!canEditSteamProfileUrl}
                              error={canEditSteamProfileUrl && steamProfileUrl === ""}
                              id="account-steam-profile-url"
                              label={ACCOUNT_VERIFICATION_STEAM_PROFILE_URL_FIELD_LABEL}
                              name="steamProfileUrl"
                              variant="outlined"
                              onChange={this.handleFormChange}
                              value={steamProfileUrl}
                              helperText={ACCOUNT_VERIFICATION_STEAM_PROFILE_URL_HELPER_TEXT}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel id="account-country-label">
                                Country
                              </InputLabel>
                              <Select
                                id="account-country"
                                name="country"
                                disabled={!canEditCountry}
                                onChange={this.handleFormChange}
                                value={country}
                                label="Country"
                              >
                                <MenuItem value="AL">Albania</MenuItem>
                                <MenuItem value="DZ">Algeria</MenuItem>
                                <MenuItem value="AD">Andorra</MenuItem>
                                <MenuItem value="AR">Argentina</MenuItem>
                                <MenuItem value="AM">Armenia</MenuItem>
                                <MenuItem value="AU">Australia</MenuItem>
                                <MenuItem value="AT">Austria</MenuItem>
                                <MenuItem value="BY">Belarus</MenuItem>
                                <MenuItem value="BE">Belgium</MenuItem>
                                <MenuItem value="BA">Bosnia and Herzegovina</MenuItem>
                                <MenuItem value="BR">Brazil</MenuItem>
                                <MenuItem value="BG">Bulgaria</MenuItem>
                                <MenuItem value="CA">Canada</MenuItem>
                                <MenuItem value="CL">Chile</MenuItem>
                                <MenuItem value="HR">Croatia</MenuItem>
                                <MenuItem value="CY">Cyprus</MenuItem>
                                <MenuItem value="CZ">Czech Republic</MenuItem>
                                <MenuItem value="DK">Denmark</MenuItem>
                                <MenuItem value="EG">Egypt</MenuItem>
                                <MenuItem value="EE">Estonia</MenuItem>
                                <MenuItem value="FI">Finland</MenuItem>
                                <MenuItem value="FR">France</MenuItem>
                                <MenuItem value="GE">Georgia</MenuItem>
                                <MenuItem value="DE">Germany</MenuItem>
                                <MenuItem value="GR">Greece</MenuItem>
                                <MenuItem value="HU">Hungary</MenuItem>
                                <MenuItem value="IS">Iceland</MenuItem>
                                <MenuItem value="IN">India</MenuItem>
                                <MenuItem value="IE">Ireland</MenuItem>
                                <MenuItem value="IQ">Iraq</MenuItem>
                                <MenuItem value="IL">Israel</MenuItem>
                                <MenuItem value="IT">Italy</MenuItem>
                                <MenuItem value="JP">Japan</MenuItem>
                                <MenuItem value="LV">Latvia</MenuItem>
                                <MenuItem value="LI">Liechtenstein</MenuItem>
                                <MenuItem value="LT">Lithuania</MenuItem>
                                <MenuItem value="LY">Libya</MenuItem>
                                <MenuItem value="LU">Luxembourg</MenuItem>
                                <MenuItem value="MT">Malta</MenuItem>
                                <MenuItem value="MX">Mexico</MenuItem>
                                <MenuItem value="MD">Moldova</MenuItem>
                                <MenuItem value="MC">Monaco</MenuItem>
                                <MenuItem value="MA">Morocco</MenuItem>
                                <MenuItem value="ME">Montenegro</MenuItem>
                                <MenuItem value="NL">Netherlands</MenuItem>
                                <MenuItem value="MK">North Macedonia</MenuItem>
                                <MenuItem value="NO">Norway</MenuItem>
                                <MenuItem value="PL">Poland</MenuItem>
                                <MenuItem value="PT">Portugal</MenuItem>
                                <MenuItem value="RO">Romania</MenuItem>
                                <MenuItem value="RU">Russia</MenuItem>
                                <MenuItem value="SA">Saudi Arabia</MenuItem>
                                <MenuItem value="RS">Serbia</MenuItem>
                                <MenuItem value="SG">Singapore</MenuItem>
                                <MenuItem value="SK">Slovakia</MenuItem>
                                <MenuItem value="SI">Slovenia</MenuItem>
                                <MenuItem value="KR">South Korea</MenuItem>
                                <MenuItem value="ES">Spain</MenuItem>
                                <MenuItem value="SE">Sweden</MenuItem>
                                <MenuItem value="CH">Switzerland</MenuItem>
                                <MenuItem value="TR">Turkey</MenuItem>
                                <MenuItem value="TN">Tunisia</MenuItem>
                                <MenuItem value="UA">Ukraine</MenuItem>
                                <MenuItem value="AE">United Arab Emirates</MenuItem>
                                <MenuItem value="GB">United Kingdom</MenuItem>
                                <MenuItem value="US">United States</MenuItem>
                              </Select>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, ml: 1.5 }}
                              >
                                Required to assign you the nearest game server locations.
                              </Typography>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 1 }}>
                            <Box className={classes.updateAvatarBox}>
                              <Typography className={classes.updateAvatarLabel}>
                                Update avatar
                              </Typography>

                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                {canEditAvatar && (
                                  <UploadButton
                                    name="avatar"
                                    onUploadComplete={this.handleFileFieldChange}
                                    onUploadStatusChange={
                                      this.handleUploadStatusChange
                                    }
                                    disableMargin
                                    hideStatus
                                    buttonText="Upload avatar"
                                  />
                                )}
                              </Box>

                              <Box sx={{ position: "relative" }}>
                                <Avatar
                                  src={avatarSrc}
                                  className={classes.existingAvatar}
                                />
                                {this.state.avatarUploadStatus?.state ===
                                  "load" && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: "50%",
                                      backgroundColor: "rgba(0,0,0,0.35)",
                                    }}
                                  >
                                    <CircularProgress
                                      size={20}
                                      className={classes.loadingAnim}
                                      style={{ width: 20, height: 20 }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            {this.state.avatarUploadStatus?.state ===
                              "error" && (
                              <FormHelperText error>
                                Avatar upload failed:{" "}
                                {this.state.avatarUploadStatus.text}
                              </FormHelperText>
                            )}
                            {this.state.avatarUploadStatus?.state ===
                              "done" && (
                              <FormHelperText>
                                Avatar uploaded. Click "Save Changes" to apply
                                it.
                              </FormHelperText>
                            )}
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 1 }}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel id="account-steam-inventory-label">
                                Steam CS Inventory
                              </InputLabel>
                              <Select
                                id="account-steam-inventory"
                                name="steamInventoryValue"
                                value={steamInventoryValue}
                                disabled={!canEditSteamInventory}
                                error={canEditSteamInventory && steamInventoryValue === ""}
                                onChange={this.handleFormChange}
                                label="Steam CS Inventory"
                              >
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="have">
                                  I have an inventory
                                </MenuItem>
                                <MenuItem value="have_considerable">
                                  I have a considerable inventory (above $500)
                                </MenuItem>
                              </Select>
                              <FormHelperText id="component-helper-text">
                                This is used for matchmaking filters
                              </FormHelperText>
                            </FormControl>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              disabled={!canEditFaceitProfileUrl}
                              id="account-faceit-profile-url"
                              label={ACCOUNT_VERIFICATION_FACEIT_FIELD_LABEL}
                              variant="outlined"
                              name="faceitProfileUrl"
                              onChange={this.handleFormChange}
                              value={faceitProfileUrl}
                              helperText={
                                ACCOUNT_VERIFICATION_FACEIT_HELPER_TEXT
                              }
                            />
                            {faceitWillTriggerReview && (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                Updating your FACEIT profile will move your account back to pending review. Admins will be notified to re-verify your profile.
                              </Alert>
                            )}
                          </Grid>

                          <Grid
                            size={{ xs: 12 }}
                            sx={{ display: "flex", alignItems: "flex-end" }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={
                                !canSaveChanges ||
                                accountUpdateDuring ||
                                this.state.avatarUploadStatus?.state ===
                                  "load"
                              }
                              onClick={this.saveChanges}
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                              {user.applicationCommunication
                                ? "Re-submit Application"
                                : "Save Changes"}
                            </Button>
                          </Grid>
                          </TierSection>

                          <TierSection
                            tier={2}
                            title="Tier 2: Basic Identity"
                            subtitle="Optional identity details that increase your profile strength."
                            defaultOpen={false}
                          >
                          <Grid size={{ xs: 12, md: 6 }} sx={{ pr: { md: 1.5 } }}>
                            <TextField
                              fullWidth
                              id="account-full-name"
                              disabled={!canEditName}
                              name="name"
                              label="Full name"
                              variant="outlined"
                              onChange={this.handleFormChange}
                              value={name}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                label="Date of birth"
                                value={dobPickerValue}
                                onChange={this.handleDateOfBirthChange}
                                disabled={!canEditDateOfBirth}
                                format="yyyy-MM-dd"
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    helperText:
                                      dobAge !== null
                                        ? `Age: ${dobAge} (auto)`
                                        : "Optional - helps increase your trust factor",
                                    id: "account-dob",
                                    name: "dateOfBirth",
                                    variant: "outlined",
                                    InputLabelProps: { shrink: true },
                                    inputProps: {
                                      readOnly: !canEditDateOfBirth,
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>

                          {/* Gender */}
                          <Grid size={{ xs: 12, md: 3 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <FormControl
                                fullWidth
                                variant="outlined"
                              >
                                <InputLabel id="account-gender-label">
                                  Gender
                                </InputLabel>
                                <Select
                                  id="account-gender"
                                  name="gender"
                                  disabled={!canEditGender}
                                  onChange={this.handleFormChange}
                                  value={gender}
                                  label="Gender"
                                >
                                  <MenuItem value="male">Male</MenuItem>
                                  <MenuItem value="female">Female</MenuItem>
                                </Select>
                              </FormControl>

                              {/* Show gender on public profile */}
                              <FormControlLabel
                                sx={{
                                  ml: 0,
                                  "& .MuiFormControlLabel-label": {
                                    whiteSpace: "nowrap",
                                  },
                                }}
                                control={
                                  <Switch
                                    checked={Boolean(showGenderOnProfile)}
                                    onChange={(e) =>
                                      this.props.actions.updateAccountFormField({
                                        name: "showGenderOnProfile",
                                        value: e.target.checked,
                                      })
                                    }
                                    name="showGenderOnProfile"
                                    disabled={!canEditShowGenderOnProfile}
                                  />
                                }
                                label="Show gender on public profile"
                              />
                            </Box>
                          </Grid>
                          </TierSection>

                          <TierSection
                            tier={3}
                            title="Tier 3: Enhanced Verification"
                            subtitle="Optional verification and profile details for a stronger trust factor."
                            defaultOpen={false}
                          >

                          <Grid size={{ xs: 12, md: 4 }} sx={{ mt: 1 }}>
                            <TextField
                              fullWidth
                              disabled={!canEditPhone}
                              id="account-phone-number"
                              label="Cell phone number"
                              variant="outlined"
                              onChange={this.handlePhoneLocalChange}
                              value={localPhoneNumber}
                              helperText="Local number — country code selected on the left"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start" sx={{ mr: 0 }}>
                                    <Select
                                      value={dialCountry}
                                      onChange={(e) =>
                                        this.handlePhoneDialCodeChange(e.target.value)
                                      }
                                      disabled={!canEditPhone}
                                      variant="standard"
                                      disableUnderline
                                      sx={{
                                        mr: 1,
                                        "& .MuiSelect-select": {
                                          py: 0,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          pr: "24px !important",
                                          fontSize: "0.95rem",
                                        },
                                      }}
                                      renderValue={(val) => {
                                        const entry = PHONE_DIAL_CODES.find(
                                          (c) => c.code === val
                                        );
                                        if (!entry) return val;
                                        return `${countryCodeToFlag(entry.code)} ${entry.dialCode}`;
                                      }}
                                      MenuProps={{
                                        PaperProps: { sx: { maxHeight: 320, maxWidth: "90vw" } },
                                      }}
                                    >
                                      {PHONE_DIAL_CODES.map((c) => (
                                        <MenuItem key={c.code} value={c.code}>
                                          <Box
                                            component="span"
                                            sx={{ mr: 1, fontSize: "1.1rem" }}
                                          >
                                            {countryCodeToFlag(c.code)}
                                          </Box>
                                          {c.name}
                                          <Box
                                            component="span"
                                            sx={{ ml: 0.75, color: "text.secondary" }}
                                          >
                                            {c.dialCode}
                                          </Box>
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          {showPhoneVerificationProviderSwitch ? (
                            <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                              >
                                Send code with
                              </Typography>
                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={selectedPhoneVerificationProvider}
                                onChange={(_event, value) => {
                                  if (value) {
                                    this.setState({
                                      phoneVerificationProvider: value,
                                    });
                                  }
                                }}
                                aria-label="Phone verification provider"
                              >
                                <ToggleButton value="twilio">
                                  Twilio SMS
                                </ToggleButton>
                                <ToggleButton value="didit">
                                  Didit.me SMS
                                </ToggleButton>
                              </ToggleButtonGroup>
                              <Typography
                                variant="caption"
                                sx={{ display: "block", mt: 0.75, color: "text.secondary" }}
                              >
                                Didn't get the SMS?{" "}
                                <Button
                                  variant="text"
                                  size="small"
                                  sx={{
                                    minWidth: 0,
                                    p: 0,
                                    textTransform: "none",
                                    fontSize: "inherit",
                                    verticalAlign: "baseline",
                                  }}
                                  onClick={() =>
                                    this.handleTryAlternatePhoneProvider(
                                      selectedPhoneVerificationProvider ===
                                        "didit"
                                        ? "twilio"
                                        : "didit"
                                    )
                                  }
                                  disabled={this.state.phoneInitiating}
                                >
                                  {selectedPhoneVerificationProvider === "didit"
                                    ? "Try Twilio instead"
                                    : "Try Didit.me instead"}
                                </Button>
                              </Typography>
                            </Grid>
                          ) : null}
                          {user &&
                          showSendPhoneVerificationCode ? (
                            <Grid size={{ xs: 12, md: 4 }} sx={{ mt: 1 }}>
                              <Box
                                sx={{
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                  width: "fit-content",
                                }}
                              >
                                <Button
                                  id="account-phone-send-code"
                                  variant="outlined"
                                  color="primary"
                                  onClick={this.handleInitiatePhoneVerification}
                                  disabled={this.state.phoneInitiating}
                                  size="small"
                                >
                                  {this.state.phoneInitiating ? (
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                  ) : null}
                                  Send Verification Code
                                </Button>
                                {this.state.phoneInitiateError ? (
                                  <FormHelperText error>
                                    {this.state.phoneInitiateError}
                                  </FormHelperText>
                                ) : null}
                              </Box>
                              </Box>
                            </Grid>
                          ) : (
                            <Grid size={{ xs: 12, md: 4 }} sx={{ mt: 1 }} />
                          )}

                          {user && phoneVerificationStatus === "verified" && (
                            <Grid size={{ xs: 12 }} sx={{ mt: 0.5, mb: 2 }}>
                              <Chip
                                icon={
                                  <CheckCircleIcon sx={{ "&&": { color: "inherit" } }} />
                                }
                                label="Phone Verified"
                                color="success"
                                variant="outlined"
                                size="small"
                              />
                            </Grid>
                          )}

                          {user && phoneVerificationStatus === "code_sent" && (
                            <Grid size={{ xs: 12 }}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  width: "100%",
                                  maxWidth: "100%",
                                  boxSizing: "border-box",
                                  overflow: "hidden",
                                  borderLeftWidth: 4,
                                  borderLeftStyle: "solid",
                                  borderLeftColor: "info.main",
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  gutterBottom
                                >
                                  Verify your phone number
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "text.secondary" }}
                                >
                                  We sent a 6-digit code by text message via{" "}
                                  {activePhoneVerificationProvider === "didit"
                                    ? "Didit.me"
                                    : "Twilio"}{" "}
                                  to the number on your profile. Enter it below
                                  to confirm your phone.
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ mb: 2, display: "block", color: "text.secondary" }}
                                >
                                  {this.state.phoneVerifySecondsLeft !== null &&
                                  this.state.phoneVerifySecondsLeft > 0
                                    ? `Code expires in ${Math.floor(this.state.phoneVerifySecondsLeft / 60)}:${String(this.state.phoneVerifySecondsLeft % 60).padStart(2, "0")}`
                                    : activePhoneVerificationProvider === "didit"
                                      ? "The code is only valid for 5 minutes."
                                      : "The code is only valid for 10 minutes."}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    width: "100%",
                                    maxWidth: "100%",
                                    flexWrap: "wrap",
                                    gap: 2,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <TextField
                                    id="account-phone-verify-code"
                                    label="6-digit code"
                                    value={this.state.phoneVerifyCode}
                                    onChange={this.handlePhoneVerifyCodeChange}
                                    inputProps={{
                                      inputMode: "numeric",
                                      pattern: "[0-9]*",
                                      maxLength: 6,
                                      "aria-label": "Phone verification code",
                                    }}
                                    disabled={this.state.phoneVerifySubmitting}
                                    size="small"
                                    sx={{ width: 168, maxWidth: "100%" }}
                                  />
                                  <Button
                                    id="account-phone-verify-submit"
                                    variant="contained"
                                    color="primary"
                                    onClick={this.handleVerifyPhoneSubmit}
                                    disabled={
                                      this.state.phoneVerifySubmitting ||
                                      this.state.phoneVerifyCode.replace(
                                        /\D/g,
                                        ""
                                      ).length !== 6
                                    }
                                  >
                                    {this.state.phoneVerifySubmitting ? (
                                      <CircularProgress
                                        size={22}
                                        sx={{ color: "#fff" }}
                                      />
                                    ) : (
                                      "Verify"
                                    )}
                                  </Button>
                                </Box>
                                {this.state.phoneVerifyError ? (
                                  <FormHelperText error sx={{ mt: 1 }}>
                                    {this.state.phoneVerifyError}
                                  </FormHelperText>
                                ) : null}
                              </Paper>
                            </Grid>
                          )}

                          <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                            <KycVerificationCard />
                          </Grid>
                          </TierSection>

                          {this.state.phoneVerifyError &&
                            phoneVerificationStatus !== "code_sent" &&
                            phoneVerificationStatus !== "expired" && (
                              <Grid size={{ xs: 12 }} sx={{ order: 301 }}>
                                <Alert severity="warning" sx={{ width: "100%" }}>
                                  {this.state.phoneVerifyError}
                                </Alert>
                              </Grid>
                            )}


                          <Grid
                            size={{ xs: 12, md: 6 }}
                            sx={{ display: "none" }}
                          >
                            <Box sx={{ display: "flex", gap: 2 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                disabled={
                                  !canSaveChanges ||
                                  accountUpdateDuring ||
                                  this.state.avatarUploadStatus?.state ===
                                    "load"
                                }
                                onClick={this.saveChanges}
                              >
                                {user.applicationCommunication
                                  ? "Re-submit Application"
                                  : "Save Changes"}
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </form>

                      {accountStatus === "verified" ? (
                        <Box sx={{ mt: 4, mb: 2 }}>
                          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 3 }} />
                          <RefragPromoNotice variant="card" />
                        </Box>
                      ) : null}

                      {/* Referral Program Section */}
                      {accountStatus === "verified" && this.state.referralData?.code && (
                        <Box sx={{ mt: 4, mb: 2 }}>
                          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: "rgba(255,255,255,0.9)" }}>
                            Referral Program
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
                            Invite friends to Ouro. When they subscribe, you get 1 free month.
                          </Typography>

                          <Box sx={{
                            display: "flex", alignItems: "center", gap: 1, mb: 1.5,
                            p: 1.5, borderRadius: 2,
                            backgroundColor: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", flex: 1 }}>
                              Your referral code: <strong style={{ color: "#fff" }}>{this.state.referralData.code}</strong>
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => this.handleCopyReferral(this.state.referralData.code, "code")}
                              sx={{ color: this.state.referralCopied === "code" ? "#4CAF50" : "rgba(255,255,255,0.5)" }}
                            >
                              {this.state.referralCopied === "code" ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                          </Box>

                          <Box sx={{
                            display: "flex", alignItems: "center", gap: 1, mb: 2,
                            p: 1.5, borderRadius: 2,
                            backgroundColor: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", flex: 1, wordBreak: "break-all" }}>
                              Personal invite link: <strong style={{ color: "#fff" }}>{buildReferralInviteLink(this.state.referralData.code)}</strong>
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => this.handleCopyReferral(buildReferralInviteLink(this.state.referralData.code), "link")}
                              sx={{ color: this.state.referralCopied === "link" ? "#4CAF50" : "rgba(255,255,255,0.5)" }}
                            >
                              {this.state.referralCopied === "link" ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                          </Box>

                          {(this.state.referralData.friendsJoined != null || this.state.referralData.friendsSubscribed != null || this.state.referralData.daysEarned != null) && (
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                              <Chip
                                label={`${this.state.referralData.friendsJoined || 0} friends joined`}
                                size="small"
                                sx={{ backgroundColor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)" }}
                              />
                              <Chip
                                label={`${this.state.referralData.friendsSubscribed || 0} subscribed`}
                                size="small"
                                sx={{ backgroundColor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)" }}
                              />
                              <Chip
                                label={`${this.state.referralData.daysEarned || 0} days earned`}
                                size="small"
                                sx={{ backgroundColor: "rgba(192, 160, 80, 0.15)", color: "#C0A050" }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Referral Code Input - for unverified users */}
                      {canEditAllFields &&
                        this.state.referralApplyStatus !== "success" && (
                        <Box sx={{ mt: 3, mb: 2 }}>
                          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 2 }} />
                          <Typography variant="body2" sx={{ mb: 1, color: "rgba(255,255,255,0.7)" }}>
                            Were you referred by a friend? Enter their code:
                          </Typography>
                          <Typography variant="caption" sx={{ mb: 1.25, color: "rgba(255,255,255,0.5)", display: "block" }}>
                            If you opened a friend's invite link, we'll fill this automatically. Apply it before verification to get 1 extra free month when you buy your first package.
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                            <TextField
                              size="small"
                              variant="outlined"
                              placeholder="e.g. OURO-XXXXXXXX"
                              value={this.state.referralCodeInput}
                              onChange={(e) => this.setState({ referralCodeInput: e.target.value })}
                              disabled={this.state.referralApplyStatus === "loading"}
                              sx={{ flex: 1, maxWidth: 280 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={this.handleApplyReferralCode}
                              disabled={!this.state.referralCodeInput.trim() || this.state.referralApplyStatus === "loading"}
                              sx={{ textTransform: "none" }}
                            >
                              {this.state.referralApplyStatus === "loading" ? (
                                <CircularProgress size={18} sx={{ color: "#fff" }} />
                              ) : "Apply"}
                            </Button>
                          </Box>
                          {this.state.referralApplyStatus === "error" && (
                            <Typography variant="caption" sx={{ color: "#f44336", mt: 0.5, display: "block" }}>
                              {this.state.referralApplyMessage}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {this.state.referralApplyStatus === "success" && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                          <Alert severity="success" sx={{ borderRadius: 2 }}>
                            {this.state.referralApplyMessage}
                          </Alert>
                        </Box>
                      )}

                      <TwoFactorSetup user={user} />

                      {/* ── Discord account linking ── */}
                      {user && (
                        <Box sx={{ mt: 4, mb: 2 }}>
                          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 3 }} />
                          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: "rgba(255,255,255,0.9)" }}>
                            Discord
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
                            Connect your Discord account to join the Ouro Discord server and receive match voice channel invites.
                          </Typography>
                          {user.discordLinked ? (
                            <Box sx={{
                              display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
                              p: 1.5, borderRadius: 2,
                              backgroundColor: "rgba(255,255,255,0.03)",
                              border: "1px solid rgba(255,255,255,0.08)",
                            }}>
                              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", flex: 1 }}>
                                Connected as{" "}
                                <strong style={{ color: "#fff" }}>
                                  {user.discordUsername || user.discordGlobalName || "Discord account"}
                                </strong>
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={this.handleDiscordUnlink}
                                disabled={this.state.discordUnlinking}
                                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                              >
                                {this.state.discordUnlinking ? (
                                  <CircularProgress size={18} sx={{ color: "inherit" }} />
                                ) : "Unlink"}
                              </Button>
                            </Box>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{ textTransform: "none" }}
                              onClick={() => {
                                window.location.href = `${basePath}/auth/discord`;
                              }}
                            >
                              Connect Discord
                            </Button>
                          )}
                        </Box>
                      )}

                      {/* ── Email notification preferences ── */}
                      {user && (
                        <Box sx={{ mt: 4, mb: 2 }}>
                          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 3 }} />
                          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: "rgba(255,255,255,0.9)" }}>
                            Email Notifications
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
                            Control non-essential emails from Ouro, like queue-open alerts, match
                            night invites, and community updates. Account, security, and payment
                            emails are always sent.
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={emailNotificationsEnabled}
                                  onChange={(e) =>
                                    this.handleEmailNotificationPreferenceChange(
                                      "nonTransactionalOptOut",
                                      e.target.checked
                                    )
                                  }
                                  disabled={this.state.emailPrefsSaving}
                                  name="emailNotificationsEnabled"
                                />
                              }
                              label="Email notifications"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={emailNotificationsEnabled && queueOpenEmailsEnabled}
                                  onChange={(e) =>
                                    this.handleEmailNotificationPreferenceChange(
                                      "queueOpenOptOut",
                                      e.target.checked
                                    )
                                  }
                                  disabled={this.state.emailPrefsSaving || !emailNotificationsEnabled}
                                  name="queueOpenEmailsEnabled"
                                />
                              }
                              label="Queue open alerts"
                            />
                          </Box>
                          {this.state.emailPrefsError ? (
                            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                              {this.state.emailPrefsError}
                            </Typography>
                          ) : null}
                        </Box>
                      )}

                      {user ? (
                        <MatchStartSoundToggle
                          user={user}
                          actions={this.props.actions}
                        />
                      ) : null}

                      {user ? (
                        <UiDesignPreferenceToggle
                          user={user}
                          actions={this.props.actions}
                        />
                      ) : null}

                      <AccountDataDeletionSection user={user} />
                    </div>
                  </>
                )}
              </Paper>
            </div>
          </div>
        </PageShell>
      </>
    );
  }
}

AccountPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object.isRequired,
};

ReadinessChecklist.propTypes = {
  refreshToken: PropTypes.any,
  accountStatus: PropTypes.string,
  phoneVerificationStatus: PropTypes.string,
};

ProfileTrustFactorBar.propTypes = {
  user: PropTypes.object,
  accountStatus: PropTypes.string,
  accountDisplayStatus: PropTypes.string,
};

TierSection.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  tier: PropTypes.number.isRequired,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
};

export {
  AccountPage,
  ReadinessChecklist,
  getReadinessSuccessCopy,
  getAccountStatusColor,
  getAccountStatusChipProps,
};
export default withStyles(styles)(AccountPage);